import { AxiosPromise } from "axios";
import { dashboardApiConfig, ops } from "./config";
import { BrandTemplateResourceApi } from "../generated/dashboard-client";
import { env } from "../env/env";
import { isStageEnv } from "../util/constants";

export type BrandTemplateSizeUnit = "IN" | "CM" | "MM" | "PX";

export interface BrandTemplateDto {
  id?: string;
  brandKitId: string;
  name: string;
  productType: string;
  createdById?: number | null;
  updatedById?: number | null;
  createdDate?: string;
  updatedDate?: string;
  width?: number | null;
  height?: number | null;
  sizeUnit?: BrandTemplateSizeUnit | null;
  lastEditorName?: string | null;
}

const api = new BrandTemplateResourceApi(dashboardApiConfig);

function timberUrl(path: string): string {
  const prefix = !isStageEnv ? env.basePath : "";
  return `${prefix}${env.timberContextPath}${path}`;
}

function timberHeaders(): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: "Bearer " + window.localStorage.getItem("tkn"),
  };
}

const BrandTemplateApi = {
  listByBrandKit(brandKitId: string): AxiosPromise<BrandTemplateDto[]> {
    return api.listByBrandKit(brandKitId, ops()) as unknown as AxiosPromise<BrandTemplateDto[]>;
  },
  async listByLocation(locationId: number): Promise<BrandTemplateDto[]> {
    const resp = await fetch(`${env.apiPath}/brand-templates/by-location?locationId=${locationId}`, {
      headers: {
        Authorization: "Bearer " + window.localStorage.getItem("tkn"),
      },
    });
    if (!resp.ok) throw new Error(`Failed to load brand templates (${resp.status})`);
    return (await resp.json()) as BrandTemplateDto[];
  },
  get(id: string): AxiosPromise<BrandTemplateDto> {
    return api.get1(id, ops()) as unknown as AxiosPromise<BrandTemplateDto>;
  },

  async saveAsBrandTemplate(designId: string): Promise<BrandTemplateDto> {
    const resp = await fetch(`${env.apiPath}/brand-templates/from-design/${designId}`, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + window.localStorage.getItem("tkn"),
      },
    });
    if (!resp.ok) throw new Error(`Failed to save as brand template (${resp.status})`);
    return (await resp.json()) as BrandTemplateDto;
  },
  removeFromBrandTemplates(id: string): AxiosPromise<void> {
    return api.removeFromBrandTemplates(id, ops()) as unknown as AxiosPromise<void>;
  },
  rename(id: string, name: string): Promise<Response> {
    return fetch(timberUrl(`/api/designs/${id}/name`), {
      method: "PUT",
      headers: timberHeaders(),
      body: JSON.stringify({ name }),
    });
  },
  copy(brandKitId: string, fromDesignId: string, name?: string): Promise<Response> {
    return fetch(timberUrl(`/api/brand-templates`), {
      method: "POST",
      headers: timberHeaders(),
      body: JSON.stringify({ brandKitId, fromDesignId, ...(name ? { name } : {}) }),
    });
  },
  copyBulk(fromDesignIds: string[], brandKitIds: string[]): Promise<Response> {
    return fetch(timberUrl(`/api/brand-templates/bulkCopy`), {
      method: "POST",
      headers: timberHeaders(),
      body: JSON.stringify({ fromDesignIds, brandKitIds }),
    });
  },
  delete(id: string): Promise<Response> {
    return fetch(timberUrl(`/api/designs/${id}`), {
      method: "DELETE",
      headers: timberHeaders(),
    });
  },
  deleteBulk(ids: string[]): Promise<Response> {
    return fetch(timberUrl(`/api/brand-templates/bulk-remove`), {
      method: "POST",
      headers: timberHeaders(),
      body: JSON.stringify({ designIds: ids }),
    });
  },
  // Bulk demote back to Saved Designs in one request (dashboard-api).
  removeFromBrandTemplatesBulk(ids: string[]): Promise<Response> {
    return fetch(`${env.apiPath}/brand-templates/bulk-remove`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + window.localStorage.getItem("tkn"),
      },
      body: JSON.stringify(ids),
    });
  },
};

export default BrandTemplateApi;
